import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';
import { DELIVERY_METHODS } from '../constants';
import { CollectionPoint } from 'lib/collection-points';
import { Address } from 'lib/address/types';
import { isEmpty, isNil, isNull } from 'lib/javascript';
import { DeliveryMethod } from '../types';
import { DONATION_TYPE, DonationType } from 'lib/donation';
import datetime from 'lib/datetime';
import { days, timeslotsByDay } from 'lib/deliveryDateTimes';
import config from 'appConfig';

interface CheckoutContextProviderProps {
  deliveryMethod: DeliveryMethod;
  setDeliveryMethod: Dispatch<SetStateAction<DeliveryMethod>>;
  deliveryDateTime: string | null;
  setDeliveryDateTime: Dispatch<SetStateAction<string | null>>;
  collectionPoint: CollectionPoint | null;
  setCollectionPoint: Dispatch<
    SetStateAction<CollectionPoint | null>
  >;
  collectionPointError: boolean;
  setCollectionPointError: Dispatch<SetStateAction<boolean>>;
  collectionPointSlot: Date | null;
  setCollectionPointSlot: Dispatch<
    SetStateAction<Date | string | null>
  >;
  isFetchingCollectionPointSlot: boolean;
  setIsFetchingCollectionPointSlot: Dispatch<SetStateAction<boolean>>;
  collectionPointAddress?: Address;
  isCollectionPointLoaded: boolean;
  needUtensils: boolean;
  setNeedUtensils: Dispatch<SetStateAction<boolean>>;
  isLeaveCoolerBag: boolean;
  setIsLeaveCoolerBag: Dispatch<SetStateAction<boolean>>;
  deliveryNotes: string;
  setDeliveryNotes: Dispatch<SetStateAction<string>>;
  hasSavedDeliveryNotes: boolean;
  saveDeliveryNotes: boolean;
  setSaveDeliveryNotes: Dispatch<SetStateAction<boolean>>;
  isAgreeToTnc: boolean;
  setIsAgreeToTnc: Dispatch<SetStateAction<boolean>>;
  donationType: DonationType;
  setDonationType: Dispatch<SetStateAction<DonationType>>;
  donationAmount: number;
  setDonationAmount: Dispatch<SetStateAction<number>>;
  shouldShowNoChangesNotification: boolean;
}

export const defaultValues: CheckoutContextProviderProps = {
  deliveryMethod: DELIVERY_METHODS.DOORSTEP,
  setDeliveryMethod: () => {},
  deliveryDateTime: null,
  setDeliveryDateTime: () => {},
  collectionPoint: null,
  setCollectionPoint: () => {},
  collectionPointError: false,
  setCollectionPointError: () => {},
  collectionPointSlot: null,
  setCollectionPointSlot: () => {},
  isFetchingCollectionPointSlot: false,
  setIsFetchingCollectionPointSlot: () => {},
  collectionPointAddress: undefined,
  needUtensils: false,
  setNeedUtensils: () => {},
  isLeaveCoolerBag: false,
  setIsLeaveCoolerBag: () => {},
  deliveryNotes: '',
  setDeliveryNotes: () => {},
  hasSavedDeliveryNotes: false,
  saveDeliveryNotes: false,
  setSaveDeliveryNotes: () => {},
  isCollectionPointLoaded: false,
  isAgreeToTnc: false,
  setIsAgreeToTnc: () => {},
  donationType: DONATION_TYPE.WITHOUT,
  setDonationType: () => {},
  donationAmount: 0,
  setDonationAmount: () => {},
  shouldShowNoChangesNotification: false,
};

export const CheckoutContext = createContext<CheckoutContextProviderProps>(
  defaultValues,
);

export const CheckoutContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [
    deliveryMethod,
    setDeliveryMethod,
  ] = useState<DeliveryMethod>(defaultValues.deliveryMethod);
  const [deliveryDateTime, setDeliveryDateTime] = useState<
    string | null
  >(null);

  const [
    collectionPoint,
    setCollectionPoint,
  ] = useState<CollectionPoint | null>(null);

  const shouldShowNoChangesNotification = useMemo(() => {
    if (
      !deliveryDateTime ||
      deliveryMethod !== DELIVERY_METHODS.DOORSTEP
    )
      return false;

    const deliveryDateTimeInTimezone = datetime(deliveryDateTime).tz(
      config.timezone,
    );

    const deliveryDayOfWeek = deliveryDateTimeInTimezone.day();
    const deliveryHour = deliveryDateTimeInTimezone.hour();
    const morningDeliveryHour = timeslotsByDay[days.sun][0].hours;

    return (
      deliveryDayOfWeek === days.sun &&
      deliveryHour === morningDeliveryHour
    );
  }, [deliveryDateTime, deliveryMethod]);

  const [
    collectionPointAddress,
    setCollectionPointAddress,
  ] = useState<Address | undefined>(undefined);

  const _setCollectionPoint = (
    collectionPoint: CollectionPoint | null,
  ) => {
    setCollectionPoint(collectionPoint);
    setCollectionPointAddress(collectionPoint?.address);
  };

  const [
    collectionPointSlot,
    setCollectionPointSlot,
  ] = useState<Date | null>(null);

  const [
    isFetchingCollectionPointSlot,
    setIsFetchingCollectionPointSlot,
  ] = useState(false);

  const [needUtensils, setNeedUtensils] = useState(false);
  const [isLeaveCoolerBag, setIsLeaveCoolerBag] = useState(false);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [saveDeliveryNotes, setSaveDeliveryNotes] = useState(false);
  const [isAgreeToTnc, setIsAgreeToTnc] = useState(false);
  const [donationType, setDonationType] = useState(
    defaultValues.donationType,
  );
  const [donationAmount, setDonationAmount] = useState(
    defaultValues.donationAmount,
  );
  const [collectionPointError, setCollectionPointError] = useState(
    false,
  );

  const isCollectionPointLoaded =
    !isFetchingCollectionPointSlot &&
    !isNull(collectionPoint) &&
    !isNull(collectionPointSlot) &&
    !isNull(collectionPointAddress);

  const contextValue = useMemo(
    () => ({
      deliveryMethod,
      setDeliveryMethod,
      deliveryDateTime,
      setDeliveryDateTime,
      collectionPoint,
      setCollectionPoint: _setCollectionPoint,
      collectionPointError,
      setCollectionPointError,
      collectionPointAddress,
      collectionPointSlot,
      setCollectionPointSlot,
      setIsFetchingCollectionPointSlot,
      isFetchingCollectionPointSlot,
      needUtensils,
      setNeedUtensils,
      isLeaveCoolerBag,
      setIsLeaveCoolerBag,
      deliveryNotes,
      setDeliveryNotes,
      hasSavedDeliveryNotes:
        !isEmpty(deliveryNotes) && !isNil(deliveryNotes),
      saveDeliveryNotes,
      setSaveDeliveryNotes,
      isCollectionPointLoaded,
      isAgreeToTnc,
      setIsAgreeToTnc,
      donationType,
      setDonationType,
      donationAmount,
      setDonationAmount,
      shouldShowNoChangesNotification,
    }),
    [
      deliveryMethod,
      deliveryDateTime,
      collectionPoint,
      collectionPointError,
      collectionPointAddress,
      collectionPointSlot,
      isFetchingCollectionPointSlot,
      needUtensils,
      isLeaveCoolerBag,
      deliveryNotes,
      saveDeliveryNotes,
      isCollectionPointLoaded,
      isAgreeToTnc,
      donationType,
      donationAmount,
      shouldShowNoChangesNotification,
    ],
  );

  return (
    <CheckoutContext.Provider value={contextValue}>
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckoutContext = () => useContext(CheckoutContext);
